// Chemin: C:\gestion-copro\src\components\layout\ApporteurLayout.tsx
// Layout pour les pages Apporteur (partenaire commercial — Alain)
// Session 188 : Création initiale

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  Target, 
  Coins,
  LogOut,
  Menu,
  X,
  Handshake,
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface ApporteurLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  description?: string;
}

// ============================================
// NAVIGATION APPORTEUR
// ============================================

const navItems: NavItem[] = [
  { 
    path: '/apporteur', 
    label: 'Mon pipeline', 
    icon: <Target size={20} />,
    description: 'Mes prospects et suivi'
  },
  { 
    path: '/apporteur/commissions', 
    label: 'Mes commissions', 
    icon: <Coins size={20} />,
    description: 'Suivi des rémunérations'
  },
];

// ============================================
// COMPOSANT
// ============================================

const ApporteurLayout: React.FC<ApporteurLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { userProfile, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-blue-50">
      {/* Header Apporteur — bleu pour distinguer des autres espaces */}
      <header className="bg-blue-800 text-white shadow-lg fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Menu burger (mobile) */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Logo Apporteur */}
          <Link to="/apporteur" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <Handshake size={24} className="text-white" />
            </div>
            <div className="hidden sm:block">
              <span className="font-bold text-lg">Copro Protect</span>
              <span className="ml-2 px-2 py-0.5 bg-blue-500 text-xs rounded-full font-medium">
                Partenaire
              </span>
            </div>
          </Link>

          {/* Actions droite */}
          <div className="flex items-center gap-4">
            {/* Profil */}
            <div className="relative group">
              <button className="flex items-center gap-2 p-2 rounded-lg hover:bg-blue-700 transition-colors">
                <span className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center font-semibold">
                  {userProfile?.prenom?.charAt(0) || 'A'}
                </span>
                <span className="hidden sm:inline text-sm">
                  {userProfile?.prenom || 'Partenaire'}
                </span>
              </button>
              
              {/* Dropdown */}
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="p-3 border-b border-gray-100">
                  <p className="font-semibold text-gray-800">
                    {userProfile?.prenom} {userProfile?.nom}
                  </p>
                  <p className="text-xs text-gray-500">{userProfile?.email}</p>
                  <p className="text-xs text-blue-600 font-medium mt-1">
                    Apporteur d'affaires
                  </p>
                </div>
                <div className="p-2">
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                  >
                    <LogOut size={16} />
                    <span>Déconnexion</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed top-[60px] left-0 bottom-0 w-72 bg-white shadow-lg z-40 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Titre section */}
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Espace partenaire
          </h2>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className={isActive ? 'text-blue-600' : 'text-gray-400'}>
                  {item.icon}
                </span>
                <div className="flex flex-col">
                  <span>{item.label}</span>
                  {item.description && (
                    <span className="text-xs text-gray-400 font-normal">
                      {item.description}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Séparateur */}
        <div className="mx-4 my-4 border-t border-gray-100" />

        {/* Info partenariat */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700 font-semibold">
              Commission : 25% du CA HT
            </p>
            <p className="text-xs text-blue-500 mt-1">
              Versement trimestriel
            </p>
          </div>
        </div>
      </aside>

      {/* Overlay mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Contenu principal */}
      <main className="lg:ml-72 pt-[60px] min-h-screen">
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default ApporteurLayout;
